import { Component, EventEmitter, Inject, OnDestroy, Output } from '@angular/core';
import { OverlayHelper } from '../../../../helper/overlay.helper';
import { Keyword, LeadDataObject } from '../../../../models/leads/leads.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BaseCustomDialog } from '../../../shared-module/dialogs/base-custom-dialog';
import { LEADS_DATA_SERVICE, LeadsDataService } from '../../../../services/leads/leads-data.service';
import { HideLoader, ShowLoader } from '../../../store/loader/loader.actions';
import { Store } from '@ngxs/store';
import { combineLatest, Observable } from 'rxjs';
import { ActionHelper } from '../../../../helper/action.helper';

@Component({
  selector: 'fi-tags',
  templateUrl: './tags.component.html',
  styleUrls: ['./tags.component.scss'],
  standalone: false,
})
export class TagsComponent extends BaseCustomDialog implements OnDestroy {

  @Output() popupClosed = new EventEmitter<boolean>();

  leadDetails: LeadDataObject;
  actionHelper = ActionHelper;
  tags: Keyword[] = [];
  tagsToAdd: Keyword[] = [];
  tagsToDelete: Keyword[] = [];
  shownPossibleTags: Keyword[] = [];

  private oldTags = new Set<Keyword>();
  private allPossibleTags: Keyword[] = [];

  constructor(
    public dialogRef: MatDialogRef<TagsComponent>,
    @Inject(LEADS_DATA_SERVICE) private leadDataService: LeadsDataService,
    @Inject(MAT_DIALOG_DATA) public dialogData: { leadDetails: LeadDataObject, tags: Keyword[] },
    private store: Store
  ) {
    super(dialogRef);
    if ( dialogData ) {
      this.leadDetails = this.dialogData.leadDetails;

      if ( dialogData.tags ) {
        this.tags = [...dialogData.tags];
        this.oldTags = new Set<Keyword>(dialogData.tags);
      }
    }

    leadDataService.getAllKeywords().subscribe(( { success, data } ) => {
      if ( success ) {
        this.shownPossibleTags = this.allPossibleTags = this.convertStringToKeyword(data);
        this.updateTags();
      }
    });

    this.updateTags();
  }

  ngOnDestroy(): void {
    OverlayHelper.unfixBody();
  }

  closeDialog( closedOnSuccess: boolean ): void {
    this.clearClassAndCloseDialog(this.dialogRef, closedOnSuccess);
  }

  onClosePopup(): void {
    this.closeDialog(false);
  }

  onOverlayClick( event: Event ): void {

    if ( event.target === event.currentTarget ) {
      this.onClosePopup();
    }
  }

  // updateTags(): void {
  //   this.tags = [...new Set(this.tags.filter(tag => tag.trim()))];
  //   this.tagsToAdd = this.tags.filter(tag => !this.oldTags.has(tag));
  //   this.tagsToDelete = [...this.oldTags].filter(tag => !this.tags.includes(tag));
  //   this.shownPossibleTags = this.allPossibleTags.filter(tag => !this.tags.includes(tag));
  // }

  onAddTag( data: string ): void {
    this.tags.push({ Text: data, Owned: true });
    this.updateTags();
    this.actionHelper.notifyAboutEdit();
  }

  updateTags(): void {
    // 1. Exact equivalent: Remove empty tags and deduplicate (based on text)
    this.tags = [...new Map(
      this.tags
        .filter(tag => tag.Text.trim())  // Use tag.text instead of tag
        .map(tag => [tag.Text, tag])     // Use text as key for deduplication
    ).values()];

    // 2. Exact equivalent: Find tags to add (present in current but not in old)
    this.tagsToAdd = this.tags.filter(tag =>
      ![...this.oldTags].some(oldTag => oldTag.Text === tag.Text)  // Compare by text
    );

    // 3. Exact equivalent: Find tags to delete (present in old but not in current)
    this.tagsToDelete = [...this.oldTags].filter(oldTag =>
      !this.tags.some(tag => tag.Text === oldTag.Text)  // Compare by text
    );

    // 4. Exact equivalent: Filter available tags (all possible except current)
    this.shownPossibleTags = this.allPossibleTags.filter(possibleTag =>
      !this.tags.some(tag => tag.Text === possibleTag.Text)  // Compare by text
    );
  }

  clearAllTags(): void {
    // Keep only the tags that are not owned (owned: false)
    this.tags = this.tags.filter(tag => !tag.Owned);

    // Tags to add should only include owned tags that were being added
    this.tagsToAdd = this.tagsToAdd.filter(tag => !tag.Owned);

    // Tags to delete should include owned tags from oldTags plus any owned tags we were going to add
    const ownedOldTags = [...this.oldTags].filter(tag => tag.Owned);
    this.tagsToDelete = [...ownedOldTags, ...this.tagsToAdd.filter(tag => tag.Owned)];

    // Show all possible tags except the ones we're keeping (non-owned)
    this.shownPossibleTags = this.allPossibleTags.filter(possibleTag =>
      !this.tags.some(tag => tag.Text === possibleTag.Text)
    );
  }

  onSubmit(): void {
    this.store.dispatch(new ShowLoader());

    const observables: Observable<any>[] = [];

    if ( this.tagsToAdd.length ) {
      const tagNames = [];
      this.tagsToAdd.map(tag => tagNames.push(tag.Text));
      observables.push(this.leadDataService.sendKeyWords(tagNames, this.leadDetails.Id));
    }

    if ( this.tagsToDelete.length ) {
      const tagNames = [];
      this.tagsToDelete.map(tag => tagNames.push(tag.Text));
      observables.push(this.leadDataService.deleteKeywords(tagNames, this.leadDetails.Id));
    }

    combineLatest(observables).subscribe(() => {
      this.store.dispatch(new HideLoader());
      this.closeDialog(true);
    })
  }

  private convertStringToKeyword( data: string[] ): Keyword[] {
    const keywords: Keyword[] = [];
    for ( let i = 0; i < data.length; i++ ) {
      keywords.push({
        Owned: true,
        Text: data[i],
      });
    }
    return keywords;
  }

}
